import { Component, OnInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { AsyncReportType, CSV_MIME_TYPE } from '@app-core/constants/constants';
import { AccessService } from '@app-core/services/access/access.service';
import { AsyncDownloadsService } from '@app-core/services/async-downloads/async-downloads.service';
import { DataService } from '@app-core/services/data/data.service';
import { GoogleTagManagerService } from '@app-core/services/google-tag-manager/google-tag-manager.service';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { DriverManagementService } from '@app-driver-management/services/driver-management.service';
import { BATCH_UNASSIGNMENT_IMG, CoachAssignmentStatus } from '@app-shared/common/batch-update.constants';
import { BulkCoachAssignmentResponse } from '@app-shared/common/coach-assignment.model';
import { SharedservicesService } from '@app-shared/services/sharedService/sharedservices.service';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-batch-coach-updation',
  templateUrl: './batch-coach-updation.component.html',
  styleUrls: ['./batch-coach-updation.component.scss'],
})
export class BatchCoachUpdationComponent implements OnInit, OnDestroy {
  @ViewChild('fileInputRef')
  public fileInputRef: ElementRef;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  public fleetId: string;
  public batchAdditionImg = BATCH_UNASSIGNMENT_IMG;
  public bulkAssignmentCsvFile: any;
  public bulkAssignmentStatus: CoachAssignmentStatus;
  public csvFileSelected = false;
  public bulkAssignmentStatusEnum = CoachAssignmentStatus;
  public driverCsvLoader: boolean;
  public currentDateFormat: string;
  public assignmentFailedDriverList: string;
  public showFileError: boolean = false;
  public loader: boolean = false;
  public csvLimitExceeds: boolean = false;
  public invalidCsvHeaders: boolean = false;
  constructor(
    private dataService: DataService,
    private sharedService: SharedservicesService,
    private accessService: AccessService,
    private gtmService: GoogleTagManagerService,
    private asyncDownloadsService: AsyncDownloadsService,
    private snackbarService: SnackBarService,
    public translate: TranslateService,
    private driverManagementService: DriverManagementService
  ) {}

  ngOnInit() {
    this.dataService._currentFleet.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.fleetId = value;
      }
    });
    this.dataService._currentDateFormat.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.currentDateFormat = value;
      }
    });
  }

  public selectCsvFile(event: any) {
    this.bulkAssignmentCsvFile = event.target.files[0];
    this.showFileError = false;
    this.bulkAssignmentStatus = null;
    this.csvFileSelected = false;
    if (!this.bulkAssignmentCsvFile) {
      return;
    }
    if (!CSV_MIME_TYPE.includes(this.bulkAssignmentCsvFile.type)) {
      this.showFileError = true;
      return;
    }
    this.csvFileSelected = true;
  }

  public batchDriverAddition() {
    if (!this.bulkAssignmentCsvFile) {
      return;
    }
    this.bulkAssignmentStatus = null;
    this.loader = true;
    this.sharedService
      .bulkAssignment(this.bulkAssignmentCsvFile)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => {
          this.loader = false;
          this.csvFileSelected = false;
          this.resetUpload();
        })
      )
      .subscribe(
        (res: BulkCoachAssignmentResponse) => {
          if (res.status === 200) {
            this.bulkAssignmentStatus = this.bulkAssignmentStatusEnum.Success;
          } else if (res.status === 202) {
            this.bulkAssignmentStatus = this.bulkAssignmentStatusEnum.PartialSuccess;
            this.assignmentFailedDriverList = res.body.csvData;
          } else {
            this.bulkAssignmentStatus = this.bulkAssignmentStatusEnum.Error;
          }
        },
        (err) => {
          if (err.status === 400 && err.error.errCode === 'LIMIT_EXCEEDS') {
            this.csvLimitExceeds = true;
          } else if (err.status === 400 && err.error.errCode === 'COLUMN_MISMATCHED') {
            this.invalidCsvHeaders = true;
          } else {
            this.bulkAssignmentStatus = this.bulkAssignmentStatusEnum.Error;
          }
        }
      );
  }
  public ExportDriverList() {
    this.driverCsvLoader = true;
    const { customerName = '' } = this.accessService.getLoginInfo();
    const params = {
      userType: 'DRIVER',
      customerName,
      dateFormat: this.currentDateFormat,
      reportType: 'FLEET_DRIVERS',
    };
    this.gtmService.exportDriversCSV(this.fleetId);
    this.asyncDownloadsService
      .requestDownloadReport(params)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        () => {
          this.driverCsvLoader = true;
        },
        (err) => {
          this.driverCsvLoader = false;
          if (err?.status === 409) {
            this.snackbarService.failure(this.translate.instant('tripListComponentTripDuplicate'));
          } else {
            this.snackbarService.failure(this.translate.instant('tripListComponentTripsDownloadFailure'));
          }
        }
      );
  }
  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
  public resetUpload() {
    this.csvFileSelected = false;
    this.bulkAssignmentCsvFile = {};
    if (this.fileInputRef) {
      this.fileInputRef.nativeElement.value = '';
    }
  }
  public downloadFailedDriverList() {
    const csvFileName = `${this.fleetId}_addition_failed_drivers.csv`;
    this.driverManagementService.downloadFile(this.assignmentFailedDriverList, csvFileName, 'text/csv;charset=utf-8;');
    this.bulkAssignmentStatus = null;
  }
  public navigateToDownloads() {
    this.asyncDownloadsService.navigateToExportHistory(AsyncReportType.drivers.key);
  }
}
